
import { useState } from "react";

const DELETE_API_URL = "https://anisso-app-9fiq2.ondigitalocean.app/api/user/deleteAccountByPassword";

export default function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  async function handleDelete(event) {
    event.preventDefault();
    setStatus(null);
    setLoading(true);

    try {
      const response = await fetch(DELETE_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          password,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.message || "Unable to delete your account."
        );
      }

      setStatus({
        type: "success",
        message:
          data.message ||
          "Your account deletion was successful.",
      });

      setEmail("");
      setPassword("");
      setConfirmed(false);
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.message ||
          "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <style>{`
        * {
          box-sizing: border-box;
        }

        body {
          margin: 0;
          font-family: Arial, Helvetica, sans-serif;
          color: #202124;
          background: #f6f7f9;
        }

        .page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px 16px;
        }

        .card {
          width: 100%;
          max-width: 460px;
          padding: 32px;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.05);
        }

        .icon {
          width: 56px;
          height: 56px;
          display: grid;
          place-items: center;
          margin-bottom: 22px;
          color: #b42318;
          background: #fef3f2;
          border-radius: 14px;
        }

        h1 {
          margin: 0 0 12px;
          font-size: 26px;
          line-height: 1.3;
        }

        .description {
          margin: 0 0 26px;
          color: #606770;
          font-size: 14px;
          line-height: 1.7;
        }

        form {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        label {
          font-size: 14px;
          font-weight: 600;
        }

        input[type="email"],
        input[type="password"],
        input[type="text"].password-input {
          width: 100%;
          min-width: 0;
          padding: 13px 14px;
          border: 1px solid #d0d5dd;
          border-radius: 8px;
          font-size: 15px;
          outline: none;
        }

        input:focus {
          border-color: #667085;
          box-shadow: 0 0 0 3px rgba(102, 112, 133, 0.12);
        }

        .password-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .password-wrapper input {
          padding-right: 65px;
        }

        .toggle-password {
          position: absolute;
          right: 8px;
          padding: 6px;
          color: #475467;
          background: transparent;
          border: none;
          font-size: 13px;
          cursor: pointer;
        }

        .confirmation {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin: 10px 0;
          color: #444;
          font-weight: 400;
          line-height: 1.6;
        }

        .confirmation input {
          flex-shrink: 0;
          margin-top: 4px;
        }

        .delete-button {
          width: 100%;
          padding: 14px;
          color: white;
          background: #b42318;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
        }

        .delete-button:hover:not(:disabled) {
          background: #912018;
        }

        .delete-button:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .status {
          padding: 12px;
          border-radius: 8px;
          font-size: 14px;
          line-height: 1.6;
          overflow-wrap: anywhere;
        }

        .success {
          color: #067647;
          background: #ecfdf3;
        }

        .error {
          color: #b42318;
          background: #fef3f2;
        }

        .note {
          margin: 22px 0 0;
          color: #606770;
          font-size: 12px;
          line-height: 1.7;
        }

        @media (max-width: 480px) {
          .card {
            padding: 24px 20px;
          }

          h1 {
            font-size: 23px;
          }
        }
      `}</style>

      <main className="page">
        <section className="card">
          <div className="icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              width="30"
              height="30"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
            >
              <path d="M3 6h18M8 6V4h8v2m3 0-1 14H6L5 6" />
            </svg>
          </div>

          <h1>Delete your account</h1>

          <p className="description">
            Enter your account credentials to request permanent
            deletion of your account and associated personal data.
            This action cannot be undone.
          </p>

          <form onSubmit={handleDelete}>
            <label htmlFor="email">Email address</label>
            <input
              id="email"
              type="email"
              placeholder="Enter your account email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <label htmlFor="password">Password</label>
            <div className="password-wrapper">
              <input
                id="password"
                className="password-input"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>

            <label className="confirmation">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                required
              />
              <span>
                I understand that my account and associated data
                may be permanently deleted.
              </span>
            </label>

            {status && (
              <div
                className={`status ${status.type}`}
                role="status"
                aria-live="polite"
              >
                {status.message}
              </div>
            )}

            <button
              type="submit"
              className="delete-button"
              disabled={
                loading ||
                !email.trim() ||
                !password ||
                !confirmed
              }
            >
              {loading ? "Processing..." : "Delete my account"}
            </button>
          </form>

          <p className="note">
            If you need help with account deletion, please contact
            our support team.
          </p>
        </section>
      </main>
    </>
  );
}
